import React from "react";
import { DocumentNode } from "graphql";
import { useMutation } from "@apollo/client";
import { dispatchEventBus } from "@catchoftheday/state";
import {
  createToast,
  updateToast,
  UpdateToastProps,
  ItemProps,
  ToastContentProps,
  Status,
} from "@catchoftheday/header-app";
import {
  AddItemToWishListDocument,
  RemoveItemFromWishListDocument,
} from "@catchoftheday/product-app";
import {
  AddToCartDocument,
  AddItemsToCartDocument,
  AddToCartInput,
  RemoveItemFromCartDocument,
  MoveItemFromCartToWishlistDocument,
  Mutation,
  CartItemError,
} from "@cart-app/generated/graphql";
import {
  UNABLE_TO_ADD_TO_CART,
  UNABLE_TO_REMOVE_ITEM,
  UNABLE_TO_ADD_TO_WISHLIST,
  UNABLE_TO_REMOVE_FROM_WISHLIST,
  GENERIC_ERROR,
} from "@cart-app/consts";

export enum MutationType {
  ADD_TO_CART = "addToCart",
  ADD_ITEMS_TO_CART = "addItemsToCart",
  REMOVE_ITEM_FROM_CART = "removeItemFromCart",
  MOVE_ITEM_FROM_CART_TO_WISHLIST = "moveItemFromCartToWishlist",
  ADD_TO_WISHLIST = "addItemToWishlist",
  ADD_TO_NOTIFY_WISHLIST = "addItemToNotifyWishlist",
  REMOVE_FROM_NOTIFY_WISHLIST = "removeItemFromNotifyWishlist",
  REMOVE_FROM_WISHLIST = "removeItemFromWishlist",
}

type ToastConfig = {
  fieldName?: string;
  mutationDocumentNode: DocumentNode;
  showCheckoutOnSuccess?: boolean;
  showTickOnSuccess?: boolean;
  eventBusMessageType?: string;
  messages: {
    loading: string;
    success: string;
    error: string;
    tertiary?: boolean;
  };
};

type UseToastProps = {
  mutationType: MutationType;
  setError?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  showCheckoutOnSuccess?: boolean;
  showTickOnSuccess?: boolean;
  noToast?: boolean;
  onCompleted?: () => void;
  fetchPolicy?: "no-cache";
};
type MutateToastProps = {
  imageUrl: string;
  title: string;
  offerId?: string;
};

const toastConfig: Record<MutationType, ToastConfig> = {
  addToCart: {
    mutationDocumentNode: AddToCartDocument,
    showCheckoutOnSuccess: true,
    eventBusMessageType: "@cart-app/ADD_TO_CART",
    messages: {
      loading: "Adding...",
      success: "Added!",
      error: UNABLE_TO_ADD_TO_CART,
    },
  },
  addItemsToCart: {
    mutationDocumentNode: AddItemsToCartDocument,
    showCheckoutOnSuccess: true,
    eventBusMessageType: "@cart-app/ADD_TO_CART",
    messages: {
      loading: "Adding...",
      success: "Added!",
      error: UNABLE_TO_ADD_TO_CART,
    },
  },
  removeItemFromCart: {
    mutationDocumentNode: RemoveItemFromCartDocument,
    showTickOnSuccess: true,
    eventBusMessageType: "@cart-app/REMOVE_ITEM_FROM_CART",
    messages: {
      loading: "Removing...",
      success: "Item removed from cart.",
      error: UNABLE_TO_REMOVE_ITEM,
    },
  },
  moveItemFromCartToWishlist: {
    mutationDocumentNode: MoveItemFromCartToWishlistDocument,
    showTickOnSuccess: true,
    eventBusMessageType: "@cart-app/ADD_TO_WISHLIST",
    messages: {
      loading: "Adding to wishlist...",
      success: "Added to wishlist!",
      error: UNABLE_TO_ADD_TO_WISHLIST,
    },
  },
  addItemToWishlist: {
    mutationDocumentNode: AddItemToWishListDocument,
    showTickOnSuccess: true,
    eventBusMessageType: "@cart-app/ADD_TO_WISHLIST",
    messages: {
      loading: "Adding to wishlist...",
      success: "Added to wishlist!",
      error: UNABLE_TO_ADD_TO_WISHLIST,
    },
  },
  addItemToNotifyWishlist: {
    fieldName: "addItemToWishlist",
    mutationDocumentNode: AddItemToWishListDocument,
    showTickOnSuccess: true,
    eventBusMessageType: "@cart-app/ADD_TO_WISHLIST",
    messages: {
      loading: "Adding to notify list...",
      success:
        "Added to Notify list! You will receive an email when this item is back in stock",
      error: UNABLE_TO_ADD_TO_WISHLIST,
      tertiary: true,
    },
  },
  removeItemFromNotifyWishlist: {
    fieldName: "addItemToWishlist",
    mutationDocumentNode: AddItemToWishListDocument,
    showTickOnSuccess: true,
    eventBusMessageType: "@cart-app/REMOVE_FROM_WISHLIST",
    messages: {
      loading: "Removing from notify list...",
      success:
        "Removed from Notify list! You will not be notified when this item is back in stock",
      error: UNABLE_TO_ADD_TO_WISHLIST,
      tertiary: true,
    },
  },
  removeItemFromWishlist: {
    mutationDocumentNode: RemoveItemFromWishListDocument,
    showTickOnSuccess: true,
    eventBusMessageType: "@cart-app/REMOVE_FROM_WISHLIST",
    messages: {
      loading: "Removing from wishlist...",
      success: "Removed from wishlist!",
      error: UNABLE_TO_REMOVE_FROM_WISHLIST,
    },
  },
};

const getResponse = (
  mutationType: string,
  data: Mutation | null | undefined
) => {
  if (!data) {
    return null;
  }
  return data?.[mutationType];
};

const noop = () => undefined;

export const useToast = <T>({
  mutationType,
  setError = noop,
  setLoading = noop,
  showCheckoutOnSuccess,
  showTickOnSuccess,
  noToast = false,
  onCompleted,
  fetchPolicy,
}: UseToastProps) => {
  const {
    fieldName,
    mutationDocumentNode,
    showCheckoutOnSuccess: defaultShowCheckoutOnSuccess,
    showTickOnSuccess: defaultShowTickOnSuccess,
    eventBusMessageType,
    messages: {
      loading: loadingMessage,
      success: successMessage,
      error: generalErrorMessage,
      tertiary,
    },
  } = toastConfig[mutationType];

  function isToastRequired(props: UpdateToastProps) {
    if (!noToast) {
      return updateToast(props);
    }
  }

  function conditionallyCreateToast(props: any) {
    if (!noToast) {
      return createToast(props);
    }
  }

  const [mutateFunction] = useMutation<Mutation, T>(mutationDocumentNode, {
    onCompleted: () => {
      if (typeof onCompleted === "function") {
        onCompleted();
      }
    },
    fetchPolicy,
  });

  const wrappedMutationFunction = async (
    ...args: Parameters<typeof mutateFunction>
  ): Promise<ReturnType<typeof mutateFunction>> => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await mutateFunction(...args);
      setLoading(false);

      const mutationResponse = getResponse(
        fieldName ?? mutationType,
        response.data
      );
      const userError =
        mutationResponse?.__typename === "UserError"
          ? mutationResponse.message
          : undefined;

      const batchUserError =
        mutationResponse?.__typename === "CartBatchMutationResponse" &&
        mutationResponse.errors.length > 0 &&
        mutationResponse.errors.find((error) => error.message)?.message;

      const errorMessage = response.errors
        ? GENERIC_ERROR
        : userError || batchUserError;
      if (errorMessage) {
        setError(errorMessage);
      }
      return response;
    } catch (e) {
      setError(GENERIC_ERROR);
      setLoading(false);
      throw e;
    }
  };

  const returnMutateFunction = async (
    items: MutateToastProps | MutateToastProps[],
    mutateVariables: T
  ) => {
    if (!Array.isArray(items)) {
      items = [items];
    }
    const toastItems: ItemProps[] = items.map((item) => ({
      message: loadingMessage,
      status: "info",
      imageUrl: item.imageUrl,
      title: item.title,
      showTickOnSuccess:
        typeof showTickOnSuccess === "boolean"
          ? showTickOnSuccess
          : defaultShowTickOnSuccess,
      offerId: item.offerId,
    }));
    const toastContent: ToastContentProps = {
      items: toastItems,
      showCheckoutOnSuccess:
        typeof showCheckoutOnSuccess === "boolean"
          ? showCheckoutOnSuccess
          : defaultShowCheckoutOnSuccess,
    };
    const toastId: any = conditionallyCreateToast({ toastContent });

    const { data, errors = [] } = await wrappedMutationFunction({
      variables: mutateVariables,
    });

    const mutateResponse = getResponse(fieldName ?? mutationType, data);

    if (errors.length > 0) {
      isToastRequired({
        toastId,
        toastContent: {
          ...toastContent,
          items: toastItems.map((toastItem) => ({
            ...toastItem,
            message: generalErrorMessage,
            status: "error",
          })),
        },
      });
      return { status: "ServerError" };
    }
    if (mutateResponse?.__typename === "UserError") {
      isToastRequired({
        toastId,
        toastContent: {
          ...toastContent,
          items: toastItems.map((toastItem) => ({
            ...toastItem,
            message: mutateResponse?.message,
            status: "error",
          })),
        },
      });
      return { status: "UserError" };
    }

    if (
      mutateResponse?.__typename === "Cart" ||
      mutateResponse?.__typename === "WishlistItem" ||
      mutateResponse?.__typename === "RemoveFromWishlistItem"
    ) {
      isToastRequired({
        toastId,
        toastContent: {
          ...toastContent,
          items: toastItems.map((toastItem) => ({
            ...toastItem,
            message: successMessage,
            status: tertiary ? "tertiary" : "success",
          })),
        },
      });
      if (eventBusMessageType) {
        dispatchEventBus({
          type: eventBusMessageType,
        });
      }
    }
    if (mutateResponse?.__typename === "CartBatchMutationResponse") {
      const errors = mutateResponse.errors as CartItemError[];
      isToastRequired({
        toastId,
        toastContent: {
          ...toastContent,
          items: toastItems.map((toastItem) => {
            let message = successMessage;
            let status: Status = "success";
            const errorItem = errors.find(
              (error) => error.offerId == toastItem.offerId
            );
            if (errorItem) {
              message = errorItem.message;
              status = "error";
            }
            return {
              ...toastItem,
              message,
              status,
            };
          }),
        },
      });
      if (eventBusMessageType) {
        dispatchEventBus({
          type: eventBusMessageType,
        });
      }
      if (errors.length) {
        return { status: errors };
      }
    }
    return { status: "OK" };
  };

  return returnMutateFunction;
};

export const useAddToCartToast = (props: Omit<UseToastProps, "mutationType">) =>
  useToast<AddToCartInput>({
    mutationType: MutationType.ADD_TO_CART,
    ...props,
  });
